import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import api from "src/utils/axios";

const initialState = {
  isLoading: false,
  error: null,
  cardItems: [],
  isPickUp: false,
  isDineIn: false,
};

const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;

const cardItemSlice = createSlice({
  name: "cardItems",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = false;
    },
    getCardItemsSuccess(state, action) {
      state.isLoading = false;
      state.cardItems = action.payload;
      state.error = null;
    },
    getCardItemsFailure(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    setPickUpAndDineIn(state, action) {
      const { isPickUp, isDineIn } = action.payload;
      state.isPickUp = isPickUp;
      state.isDineIn = isDineIn;
    },
  },
});

export default cardItemSlice.reducer;

export const { getCardItemsSuccess, getCardItemsFailure, setPickUpAndDineIn } = cardItemSlice.actions;

export function addItemsToCard(payload) {
  return async (dispatch, getState) => {
    dispatch(cardItemSlice.actions.startLoading());

    try {
      const state = getState().cardSlice;
      const currentItems = state.cardItems?.items || [];
      const newItems = payload.items || [];
      const updatedItems = [...currentItems, ...newItems];
      const updatedCardItems = { ...state.cardItems, items: updatedItems };
      dispatch(cardItemSlice.actions.getCardItemsSuccess(updatedCardItems));
    } catch (error) {
      dispatch(
        cardItemSlice.actions.getCardItemsFailure(
          error.message || "Failed to add item"
        )
      );
    }
  };
}

export function updateItemsToCard(payload) {
  return async (dispatch, getState) => {
    dispatch(cardItemSlice.actions.startLoading());
    try {
      const state = getState().cardSlice;
      const currentItems = state.cardItems?.items || [];
      const newItems = payload.items || [];
      const updatedItems = [...currentItems];
      newItems.forEach((newItem) => {
        const index = updatedItems.findIndex(
          (item) => item.cartItemId === newItem.cartItemId
        );

        if (index !== -1) {
          updatedItems[index] = newItem;
        } else {
          updatedItems.push(newItem);
        }
      });

      const updatedCardItems = {
        ...state.cardItems,
        items: updatedItems,
      };

      dispatch(cardItemSlice.actions.getCardItemsSuccess(updatedCardItems));
    } catch (error) {
      dispatch(
        cardItemSlice.actions.getCardItemsFailure(
          error.message || "Failed to update items"
        )
      );
    }
  };
}


export function updateItemsToCardByIncDec(item) {
  return async (dispatch) => {
    dispatch(cardItemSlice.actions.startLoading());
    try {
      dispatch(cardItemSlice.actions.getCardItemsSuccess(item));
    } catch (error) {
      throw error;
    }
  };
}

export function getCartByCustomerId() {
  return async (dispatch) => {
    dispatch(cardItemSlice.actions.startLoading());
    try {
      dispatch(cardItemSlice.actions.getCardItemsSuccess());
    } catch (error) {
      dispatch(cardItemSlice.actions.getCardItemsSuccess([]));
      return error;
    }
  };
}

export function deleteCartBySessionId(sessionId) {
  return async (dispatch) => {
    dispatch(cardItemSlice.actions.startLoading());
    try {
      const response = await axios.delete(
        `${backendUrl}/cart/deleteCartBySessionId?sessionId=${sessionId}`
      );
      dispatch(cardItemSlice.actions.getCardItemsSuccess(response.data));
      return { data: response?.data, status: response.status };
    } catch (error) {
      dispatch(
        cardItemSlice.actions.getCardItemsFailure(error.response?.data?.messags)
      );
    }
  };
}

export function removeItemFromCart(cartItemId) {
  return async (dispatch, getState) => {
    dispatch(cardItemSlice.actions.startLoading());
    const currentState = getState().cardSlice;
    try {
      const updatedCartItems = currentState.cardItems.items.filter(
        (item) => item.cartItemId !== cartItemId
      );
      const newCardItems = {
        ...currentState.cardItems,
        items: updatedCartItems,
      };
      dispatch(cardItemSlice.actions.getCardItemsSuccess(newCardItems));
    } catch (error) {
      dispatch(
        cardItemSlice.actions.getCardItemsFailure(
          error?.message || "An error occurred"
        )
      );
    }
  };
}

export function addTipToCart(tip) {
  return async (dispatch, getState) => {
    dispatch(cardItemSlice.actions.startLoading());
    const formattedTip = parseFloat(tip).toFixed(2);
    try {
      const currentState = getState().cardSlice;
      const cartId = currentState.cardItems?.data?.id;

      const response = await axios.patch(
        `${backendUrl}/cart/${cartId}`,
        {
          tip: formattedTip,
        }
      );

      const updatedCart = {
        ...currentState.cardItems,
        data: {
          ...currentState.cardItems.data,
          tip: formattedTip,
        },
      };

      dispatch(cardItemSlice.actions.getCardItemsSuccess(updatedCart));
      return { data: response?.data, status: response.status };
    } catch (error) {
      dispatch(
        cardItemSlice.actions.getCardItemsFailure(
          error.response?.data?.message || "An error occurred"
        )
      );
      return error;
    }
  };
}

export function clearCart() {
  return async (dispatch, getState) => {
    dispatch(cardItemSlice.actions.startLoading());
    try {
      const currentState = getState().cardSlice;

      const clearedCart = {
        ...currentState.cardItems,
        items: [], // Clear the items
      };

      dispatch(cardItemSlice.actions.getCardItemsSuccess(clearedCart));
    } catch (error) {
      dispatch(
        cardItemSlice.actions.getCardItemsFailure(
          error?.message || "Failed to clear the cart"
        )
      );
    }
  };
}

export function refreshStoreCartPromotion(
  cartId,
  orderType
) {
  return async (dispatch) => {
    dispatch(
      cardItemSlice.actions.startLoading()
    );

    try {
      const response = await api.patch(
        `${backendUrl}/store/refreshCartPromotion/${cartId}`,
        {
          type: 'store',
          orderType,
        }
      );

      dispatch(
        cardItemSlice.actions
          .getCardItemsSuccess(
            response.data
          )
      );

      return response;
    } catch (error) {
      dispatch(
        cardItemSlice.actions
          .getCardItemsFailure(
            error?.response?.data
              ?.message ||
            'Unable to refresh promotion'
          )
      );

      throw error;
    }
  };
}